import { Box, Button, Chip, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import { theme } from "src/theme/mui-theme";
import { CloseSvgIcon } from "src/components/AppHeader/icons/CloseSvgIcon";
import { useState } from "react";
import { ts, ts2 } from "src/utils/translate";
import {
    CreateStripeSubscriptionResponse,
    useCancelStripeSubscriptionMutation,
    useChangeSubscriptionBankCardMutation,
    useCreateStripePaymentMutation,
    useCreateStripeSubscriptionMutation,
    useValidatePaymentCouponMutation
} from "src/api/v1/payment-ApiSlice";
import { useGetPaymentStatusQuery } from "src/api/v2/payment-v2-ApiSlice";
import { useGetBillingDataQuery } from "src/api/v2/user-v2-ApiSlice";
import { showSnackBar } from "src/api/ui-slice";
import { useAppDispatch } from "src/app/hooks";
import { BillingDataDialog } from "src/components/AppHeader/BillingDataDialog";

interface SubscriptionModalProps {
    open: boolean;
    onClose: () => void;
}

enum PurchaseKind {
    SUBSCRIPTION = 'subscription',
    ONE_TIME = 'oneTime',
}

interface PendingPurchase {
    kind: PurchaseKind;
    monthQuantity?: number;
}

export const SubscriptionModal = ({ open, onClose }: SubscriptionModalProps) => {

    const { data: paymentStatus } = useGetPaymentStatusQuery();
    const [cancelStripeSubscription, { isLoading: isCancelLoading }] = useCancelStripeSubscriptionMutation();
    const [createStripeSubscription, { isLoading: isCreateLoading }] = useCreateStripeSubscriptionMutation();
    const [createStripePayment, { isLoading: isPaymentLoading }] = useCreateStripePaymentMutation();
    const [changeSubscriptionBankCard] = useChangeSubscriptionBankCardMutation();
    const [validatePaymentCoupon] = useValidatePaymentCouponMutation();
    const [code, setCode] = useState(paymentStatus?.prices['coupon_code_txt'] ?? "");

    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [pendingPurchase, setPendingPurchase] = useState<PendingPurchase | null>(null);

    const dispatch = useAppDispatch();

    const showError = (text: string) => dispatch(showSnackBar({ text, severity: 'error' }));

    const { data: billingData, isLoading: isBillingDataLoading } = useGetBillingDataQuery(undefined, { skip: !open });

    const handleSubscribe = () => {
        if (!billingData?.is_filled) {
            setPendingPurchase({ kind: PurchaseKind.SUBSCRIPTION });
            return;
        }
        startSubscribe();
    };

    const startSubscribe = async () => {
        try {
            redirectToCheckout(await createStripeSubscription({ redirectToUI: true }).unwrap());
        } catch {
            showError(ts("payment_could_not_be_started", "The payment could not be started, please try again."));
        }
    };

    const redirectToCheckout = (res: CreateStripeSubscriptionResponse | string) => {
        const session: CreateStripeSubscriptionResponse =
            typeof res === 'string' ? JSON.parse(res) : res;

        if (!session.url) {
            throw new Error("Stripe session URL not found");
        }
        window.location.href = session.url;
    };

    const handlePayment = (monthQuantity: number) => {
        if (!billingData?.is_filled) {
            setPendingPurchase({ kind: PurchaseKind.ONE_TIME, monthQuantity });
            return;
        }
        startPayment(monthQuantity);
    };

    const startPayment = async (monthQuantity: number) => {
        try {
            redirectToCheckout(await createStripePayment({ monthQuantity, redirectToUI: true }).unwrap());
        } catch {
            showError(ts("payment_could_not_be_started", "The payment could not be started, please try again."));
        }
    };

    const handleBillingDataSaved = () => {
        const purchase = pendingPurchase;
        setPendingPurchase(null);

        if (purchase?.kind === PurchaseKind.SUBSCRIPTION) {
            startSubscribe();
        } else if (purchase?.kind === PurchaseKind.ONE_TIME && purchase.monthQuantity != null) {
            startPayment(purchase.monthQuantity);
        }
    };

    const handleChangeCard = async () => {
        try {
            const url = await changeSubscriptionBankCard().unwrap();
            if (url) {
                window.location.href = url;
            }
        } catch {
            showError(ts("something_went_wrong_try_again", "Something went wrong, please try again."));
        }
    };

    const handleValidateCoupon = async (couponCode: string) => {
        try {
            const response = await validatePaymentCoupon({ couponCode }).unwrap();
            if (!response.valid_register_coupon && !response.valid_payment_subscription_coupon) {
                showError(response.error_message);
            }
        } catch {
            showError(ts("something_went_wrong_try_again", "Something went wrong, please try again."));
        }
    };

    const handleClose = () => {
        setCode("");
        setPendingPurchase(null);
        onClose();
    };

    if (!paymentStatus) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            sx={{ "& .MuiPaper-root": { p: 4, borderRadius: 2 } }}
        >
            <CloseSvgIcon sx={{ position: "absolute", right: 10, top: 10, cursor: "pointer", fontSize: "40px" }} onClick={handleClose}/>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
                <Typography color={theme.palette.primary.main} fontWeight={600} variant="h4">
                    { ts("license_details", "License Details") }
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography fontWeight={500} variant="h6">
                            {ts("pro_license", "Pro license")}
                        </Typography>
                        <Chip
                            label={
                                paymentStatus.has_volume_license ||
                                paymentStatus.is_active_payment ||
                                paymentStatus.is_active_subscription
                                    ? ts("active", "Active")
                                    : ts("inactive", "Inactive")
                            }
                            sx={{
                                bgcolor:
                                    paymentStatus.has_volume_license ||
                                    paymentStatus.is_active_payment ||
                                    paymentStatus.is_active_subscription
                                        ? theme.palette.success.main
                                        : theme.palette.error.main,
                                color: "#fff",
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 2,
                            }}
                            size="small"
                        />
                    </Box>

                    {paymentStatus.has_ongoing_sub && (
                            <Button
                                variant="outlined"
                                // sx={{
                                //     color: theme.palette.error.main,
                                //     borderColor: theme.palette.error.main,
                                //     ":hover": {
                                //         bgcolor: theme.palette.error.main,
                                //         color: "#fff",
                                //         borderColor: theme.palette.error.main,
                                //     },
                                // }}
                                size="small"
                                disabled={isCancelLoading}
                                onClick={() => setIsCancelDialogOpen(true)}
                            >
                                {ts("cancel_subscription", "Cancel subscription")}
                            </Button>
                        )}
                </Box>
            </Box>

            {paymentStatus.has_ongoing_sub && (
                <Box>
                    <Typography color={theme.palette.primary.main} fontWeight={600} variant="h4">
                        { ts("change_bank_card_details", "Change bank card details") }
                    </Typography>

                    <Typography variant="body2">
                        { ts("modify_card_details_or_retry_payment_text", "Modify bank card details or retry payment. In case of unsuccessful payment, we will automatically retry to deduct the amount in the following days.") }
                    </Typography>

                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2,
                            marginX: "auto",
                            marginTop: 1,
                            ":hover": { opacity: 0.9 },
                        }}
                        disabled={false}
                        onClick={handleChangeCard}
                    >
                        { ts("change_bank_card_details", "Change bank card details") }
                    </Button>
                </Box>
            )}

            <Typography
                align="center"
                variant="h3"
                sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 4, marginTop: 3 }}
            >
                { ts("explore_our_prices", "Explore Our Prices") }
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 3,
                    mb: 4,
                }}
            >
                {/* Monthly */}
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        borderRadius: 2,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        gap: 1,
                        position: "relative",
                    }}
                >
                    {paymentStatus.coupon && (
                        <Chip
                            label={
                                <Typography variant="body1" fontWeight={600}>
                                    {paymentStatus.prices["coupon_discount_label"]}
                                </Typography>
                            }
                            sx={{
                                position: "absolute",
                                top: -20,
                                right: 20,
                                bgcolor: theme.palette.primary.main,
                                color: "#fff",
                                fontWeight: 600,
                                borderRadius: 3,
                                height: 40,
                                width: 130,
                            }}
                            size="small"
                        />
                    )}
                    <Typography fontWeight={600} variant="h4" color={theme.palette.primary.main}>
                        <span style={{ fontSize: "1.2rem" }}>€</span>
                        {paymentStatus.coupon
                            ? paymentStatus.prices["calculated_subscribe_price"]
                            : paymentStatus.subscribe_price}
                        <span style={{ fontSize: "1.2rem", verticalAlign: "super" }}>*</span>
                        <span style={{ fontSize: "1rem", fontWeight: 500 }}>
                            /{ts("month", "month")}
                          </span>
                    </Typography>
                    <Typography variant="caption" fontWeight="500" sx={{ ml: "35px" }}>
                        {paymentStatus.prices["hasTax"] && (
                            <>* +{ts("vat", "VAT")}</>
                        )}
                    </Typography>
                    <Typography variant="caption" fontWeight="500">
                        {ts("renews_automatically", "Renews automatically")}
                        {paymentStatus.coupon && (
                            <> {ts("at_full_price", "at full price")} €{paymentStatus.subscribe_price}*</>
                        )}
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            textTransform: "none",
                            borderRadius: 2,
                            px: 7,
                            mx: "auto",
                            mt: 1,
                            ":hover": { opacity: 0.9 },
                        }}
                        disabled={
                            paymentStatus.is_active_subscription ||
                            paymentStatus.is_active_payment ||
                            isCreateLoading ||
                            isBillingDataLoading
                        }
                        onClick={handleSubscribe}
                    >
                        {ts("purchase", "Purchase")}
                    </Button>

                    {(paymentStatus.is_active_payment ||
                        paymentStatus.is_active_subscription) && (
                        <Typography variant="caption" fontWeight="500">
                            {ts(
                                "you_have_active_pro_account_until",
                                "You have an active Pro account until"
                            ) + " " + paymentStatus.pro_license_date}
                        </Typography>
                    )}
                </Box>

                {/* Annual */}
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        borderRadius: 2,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        gap: 1,
                        position: "relative",
                    }}
                >
                    <Chip
                        label={
                            <Typography variant="body1" fontWeight={600}>
                                {paymentStatus.coupon
                                    ? paymentStatus.prices["coupon_discount_label"]
                                    : ts("best_offer", "Best offer")}
                            </Typography>
                        }
                        sx={{
                            position: "absolute",
                            top: -20,
                            right: 20,
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            fontWeight: 600,
                            borderRadius: 3,
                            height: 40,
                            width: 130,
                        }}
                        size="small"
                    />
                    <Typography fontWeight={600} variant="h4" color={theme.palette.primary.main}>
                        <span style={{ fontSize: "1.2rem" }}>€</span>
                        {paymentStatus.prices["calculated_twelve_price_per_month"]}
                        <span style={{ fontSize: "1.2rem", verticalAlign: "super" }}>*</span>
                        <span style={{ fontSize: "1rem", fontWeight: 500 }}>
                            /{ts("month", "month")}
                          </span>
                    </Typography>
                    <Typography variant="caption" fontWeight="500" sx={{ ml: "35px" }}>
                        {paymentStatus.prices["hasTax"] && <>* +{ts("vat", "VAT")}</>}
                    </Typography>
                    <Typography variant="caption" fontWeight="500">
                        {ts2(
                            "billed_annually_price_year",
                            "Billed annually € {price} /year",
                            { price: String(paymentStatus.prices["calculated_twelve_price"] ?? "") }
                        )}
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            textTransform: "none",
                            borderRadius: 2,
                            px: 7,
                            mx: "auto",
                            mt: 1,
                            ":hover": { opacity: 0.9 },
                        }}
                        disabled={isPaymentLoading || isBillingDataLoading}
                        onClick={() => handlePayment(12)}
                    >
                        {ts("purchase", "Purchase")}
                    </Button>

                    {(paymentStatus.is_active_payment ||
                        paymentStatus.is_active_subscription) && (
                        <Typography variant="caption" fontWeight="500">
                            {ts(
                                "you_have_active_pro_account_until",
                                "You have an active Pro account until"
                            ) + " " + paymentStatus.pro_license_date}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Coupon Code */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginX: "auto",
                    width: 500,
                    gap: 1,
                }}
            >
                <Typography variant="h6" fontWeight="600">
                    { ts("coupon_code", "Coupon code") }:
                </Typography>

                <Box sx={{ display: "flex", width: "100%", gap: 3 }}>
                    <TextField
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder={ ts("enter_code", "Enter code") }
                        size="small"
                        // sx={{
                        //     flex: 1,
                        //     "& .MuiOutlinedInput-root": {
                        //         borderRadius: 2,
                        //     },
                        //     "& .MuiInputBase-input": {
                        //         textAlign: "center",
                        //     },
                        // }}
                    />
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: "#fff",
                            textTransform: "none",
                            borderRadius: 2,
                            px: 7,
                            py: 1,
                            ":hover": { opacity: 0.9 },
                        }}
                        onClick={() => handleValidateCoupon(String(code))}
                    >
                        { ts("activate", "Activate") }
                    </Button>
                </Box>

            </Box>


            {/* Info text */}
            <Typography
                align="center"
                variant="h6"
                sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    mt: 4,
                    mb: 4
                }}
            >
                { ts("pro_package_not_include_electrical_wiring_diagram", "The PRO package does not include the electrical wiring diagram.") }
                <br />
                { ts("need_to_be_purchased_separately", "It needs to be purchased separately.") }
            </Typography>

            <Box
                sx={{
                    border: "1px solid",
                    borderColor: theme.palette.primary.main,
                    borderRadius: 2,
                    p: 2,
                    pb: 4,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography>• {ts("unlimited_projects", "Unlimited projects")}</Typography>
                    <Typography>• {ts("unlimited_plan_project", "Unlimited plan/project")}</Typography>
                    <Typography>• {ts("unlimited_members_projects", "Unlimited members/projects")}</Typography>
                    <Typography>• {ts("bill_of_materials", "Bill of Materials")}</Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography>• {ts("wifi_camera_heatmap", "Wifi & Camera Heatmap")}</Typography>
                    <Typography>• {ts("backbox_calculation", "Backbox calculation")}</Typography>
                    <Typography>• {ts("loxone_config_export", "Loxone Config export")}</Typography>
                    <Typography>• {ts("cable_list", "Cable list")}</Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography>• {ts("article_numbers", "Article numbers")}</Typography>
                    <Typography>• {ts("mmanufacturer_discounts", "Manufacturer discounts")}</Typography>
                    <Typography>• {ts("custom_items", "Custom items")}</Typography>
                    <Typography>• {ts("customised_offer", "Customised offer")}</Typography>
                </Box>
            </Box>

            <Dialog
                open={isCancelDialogOpen}
                onClose={() => setIsCancelDialogOpen(false)}
                sx={{ "& .MuiPaper-root": { p: 4, borderRadius: 2, minWidth: 400 } }}
            >
                <DialogContent sx={{ textAlign: "center" }}>
                    <Typography variant="h6" fontWeight={600}>
                        { ts("you_want_to_cancel_subscription_question", "Are you sure you want to cancel your subscription") }?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setIsCancelDialogOpen(false)}
                    >
                        { ts("plt_cancel", "Cancel") }
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            cancelStripeSubscription().unwrap()
                                .then(() => setIsCancelDialogOpen(false))
                                .catch(() => showError(
                                    ts("something_went_wrong_try_again", "Something went wrong, please try again.")));
                        }}
                    >
                        { ts("plt_confirm", "Confirm") }
                    </Button>
                </DialogActions>
            </Dialog>

            <BillingDataDialog
                open={pendingPurchase !== null}
                onClose={() => setPendingPurchase(null)}
                onSaved={handleBillingDataSaved}
            />
        </Dialog>
    );
};
